import React, { useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import useColumnState from "../hooks/useColumnState";

/**
 * Reusable AgGrid component with persistent column state management
 * @param {object} props - AgGridReact props plus additional configuration
 * @param {string} props.tableId - Unique identifier for the table to store state
 * @param {function} props.onGridReady - Custom onGridReady handler (optional)
 * @param {object} props.defaultColDef - Default column definitions (will be merged with column state settings)
 * @param {boolean} props.enableColumnState - Whether to enable column state persistence (default: true)
 * @param {object} props.gridApiRef - External ref for grid API (optional)
 * @returns {JSX.Element} AgGridReact component with column state management
 */
const AgGridWithColumnState = ({
  tableId,
  onGridReady: customOnGridReady,
  onFirstDataRendered: customOnFirstDataRendered,
  defaultColDef = {},
  enableColumnState = true,
  gridApiRef: externalGridApiRef,
  columnDefs,
  ...agGridProps
}) => {
  const {
    gridApiRef,
    columnApiRef,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onColumnEvent,
    onFilterChanged,
    restoreColumnState,
    savedColumnState,
  } = useColumnState(enableColumnState ? tableId : null);

  // Reapply column state when savedColumnState changes (e.g., after login when user ID becomes available)
  // NOTE: Removed columnDefs from dependencies - we don't want to reset sort when columns re-render
  // The grid maintains its own sort state, we only need to restore from localStorage on mount or user change
  const hasAppliedInitialStateRef = React.useRef(false);
  
  useEffect(() => {
    // Only apply saved state ONCE on initial load or when savedColumnState changes due to user login
    // Don't re-apply on every columnDefs change as that would reset the current sort
    if (
      enableColumnState &&
      gridApiRef.current &&
      savedColumnState &&
      Array.isArray(savedColumnState) &&
      savedColumnState.length > 0 &&
      !hasAppliedInitialStateRef.current
    ) {
      // Small delay to ensure grid has processed initial render
      const timeoutId = setTimeout(() => {
        try {
          if (gridApiRef.current) {
            gridApiRef.current.applyColumnState({
              state: savedColumnState,
              applyOrder: true,
              // Don't reset sort for columns not in saved state - preserve current sort
            });
            hasAppliedInitialStateRef.current = true;
          }
        } catch (error) {
          console.warn(
            "Failed to apply saved column state:",
            error
          );
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [enableColumnState, savedColumnState]);

  // Enhanced default column definitions with column state support
  const enhancedDefaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 50,
    lockPosition: false, // Allow column moving
    ...defaultColDef,
  };

  // Combined grid ready handler
  const handleOnGridReady = (params) => {
    // Set external ref if provided
    if (externalGridApiRef) {
      externalGridApiRef.current = params.api;
    }

    // Handle column state management if enabled
    if (enableColumnState) {
      handleGridReady(params);
    }

    // Call custom onGridReady if provided
    if (customOnGridReady) {
      customOnGridReady(params);
    }
  };

  // Combined first data rendered handler
  const handleOnFirstDataRendered = (params) => {
    // Handle column state management if enabled
    if (enableColumnState) {
      handleFirstDataRendered(params);
    }

    // Call custom onFirstDataRendered if provided
    if (customOnFirstDataRendered) {
      customOnFirstDataRendered(params);
    }
  };

  // Column event handlers (only if column state is enabled)
  // Pass the event object so we can detect the type of change
  const columnEventHandlers = enableColumnState
    ? {
        onColumnMoved: (e) => onColumnEvent(e),
        onColumnResized: (e) => onColumnEvent(e),
        onColumnVisible: (e) => onColumnEvent(e),
        onColumnPinned: (e) => onColumnEvent(e),
        onSortChanged: (e) => onColumnEvent({ ...e, type: 'sortChanged' }),
        onFilterChanged: onFilterChanged,
      }
    : {};

  return (
    <AgGridReact
      defaultColDef={enhancedDefaultColDef}
      suppressMovableColumns={!enableColumnState} // Enable/disable column moving based on column state
      animateRows={true}
      onGridReady={handleOnGridReady}
      onFirstDataRendered={
        enableColumnState
          ? handleOnFirstDataRendered
          : customOnFirstDataRendered
      }
      columnDefs={columnDefs}
      {...columnEventHandlers}
      {...agGridProps}
    />
  );
};

export default AgGridWithColumnState;
